import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { LoginResponse } from '../../interfaces/models';
import { AuthService } from '../../providers/auth/auth.service';
import { LoadingService } from '../../providers/loading/loading.service';
import { UserService } from '../../providers/user/user.service';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { environment } from '../../../environments/environment';
import { DirectivesModule } from '../../directives.module';
import { EMPTY, catchError, finalize } from 'rxjs';

const RECAPTCHA_ERROR_MESSAGE = 'Security check failed. Please refresh and try again.';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    DirectivesModule,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent {
  public isSubmitting = false;
  public loginErrorMessage = '';

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private loadingService: LoadingService,
    private recaptchaV3Service: ReCaptchaV3Service,
  ) {}

  public data = {
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required]),
    remember: false,
  };

  private validateKeys(keys: string[]): boolean {
    return keys.some(key => !this.data[key].valid);
  }

  public validateFields() {
    return this.validateKeys(['email', 'password']);
  }

  public goRegister() {
    this.router.navigateByUrl('/register');
  }

  public login() {
    if (this.validateFields() || this.isSubmitting) {
      return;
    }
    this.loginErrorMessage = '';
    if (environment.recatchaEnabled) {
      if (!this.recaptchaV3Service || typeof this.recaptchaV3Service.execute !== 'function') {
        this.loginErrorMessage = RECAPTCHA_ERROR_MESSAGE;
        return;
      }
      this.recaptchaV3Service
        .execute('login')
        .pipe(
          catchError(() => {
            this.loginErrorMessage = RECAPTCHA_ERROR_MESSAGE;
            this.loadingService.stopLoading();
            this.isSubmitting = false;
            return EMPTY;
          }),
        )
        .subscribe(this.doLogin.bind(this));
      return;
    }
    this.doLogin();
  }

  private doLogin(token?: string) {
    this.loginErrorMessage = '';
    this.isSubmitting = true;
    this.loadingService.startLoading();
    this.authService
      .authLoginPost({
        token,
        email: this.data.email.value,
        password: this.data.password.value,
      })
      .pipe(
        catchError((error: { message?: string }) => {
          this.loginErrorMessage =
            error?.message || 'Login failed. Please check credentials and try again.';
          return EMPTY;
        }),
        finalize(() => {
          this.isSubmitting = false;
          this.loadingService.stopLoading();
        }),
      )
      .subscribe((res: LoginResponse) => {
        this.userService.setTokens(res, this.data.remember);
        this.userService.getMe().subscribe((user) => {
          this.userService.setAuthenticatedUser(user);
          this.router.navigateByUrl(this.userService.getLandingRoute(user.roleId));
        });
      });
  }

  public rememberMe(event: { checked: boolean }) {
    this.data.remember = event.checked;
  }
}
